import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/guagua';
import { formatViews, getCategory } from '@/utils/content';

type NewsCardProps = {
  article: Article;
  variant?: 'default' | 'horizontal' | 'compact';
};

function CategoryBadge({ slug }: { slug: string }) {
  const category = getCategory(slug);
  if (!category) return null;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-500 text-background-50 whitespace-nowrap">
      {category.name}
    </span>
  );
}

export default function NewsCard({ article, variant = 'default' }: NewsCardProps) {
  if (variant === 'compact') {
    return (
      <article className="group">
        <Link
          to={`/news/${article.id}`}
          className="flex items-start gap-3 py-3 cursor-pointer"
        >
          <span className="mt-0.5 text-primary-500">
            <i className="ri-chat-forward-line" aria-hidden="true"></i>
          </span>
          <span className="flex-1">
            <span className="block text-sm md:text-base font-medium text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
              {article.title}
            </span>
            <span className="block mt-1 text-xs text-foreground-400">
              {article.author} · {article.date}
            </span>
          </span>
        </Link>
      </article>
    );
  }

  if (variant === 'horizontal') {
    return (
      <article className="group">
        <Link
          to={`/news/${article.id}`}
          className="flex gap-4 cursor-pointer"
        >
          <div className="relative w-28 h-20 sm:w-36 sm:h-24 shrink-0 rounded-md overflow-hidden bg-background-200">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - 瓜哥吃瓜`}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <CategoryBadge slug={article.categorySlug} />
              <span className="text-xs text-foreground-400">{article.date}</span>
            </div>
            <h3 className="font-heading text-sm sm:text-base font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
              {article.title}
            </h3>
            <p className="hidden sm:block mt-1.5 text-xs text-foreground-500 line-clamp-1">
              {article.excerpt}
            </p>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article className="group h-full">
      <Link
        to={`/news/${article.id}`}
        className="flex flex-col h-full rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
      >
        <div className="relative w-full h-44 md:h-48 overflow-hidden bg-background-200">
          <img
            src={article.image}
            alt={article.title}
            title={`${article.title} - 瓜哥吃瓜`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          <span className="absolute top-3 left-3">
            <CategoryBadge slug={article.categorySlug} />
          </span>
        </div>
        <div className="flex flex-col flex-1 p-4 md:p-5">
          <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2 flex-1">
            {article.excerpt}
          </p>
          <div className="mt-3 pt-3 border-t border-background-200 flex items-center justify-between text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-user-3-line" aria-hidden="true"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <i className="ri-eye-line" aria-hidden="true"></i>
                {formatViews(article.views)}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-chat-3-line" aria-hidden="true"></i>
                {article.comments}
              </span>
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}